/**
 * PaylasimliBelgeKararParcalari.jsx — paylaşımlı belgenin (SGK Hizmet Dökümü 96 / İSG Katılım Listesi 98) PERSONEL tarafı tarih ve
 * dönem parçaları. Backlog #297 (geçerlilik tarihi) ve #268 (Hizmet Dökümü dönemi).
 *
 * Firma belgeyi tarihsiz yükler (backend #295); ne zamana kadar sayılacağını onaylayan personel belirler, Hizmet Dökümü'nde ayrıca
 * belgenin kapsadığı ayı (#267). İkisi de BELGENİN kendisine yazılır ve belgedeki tüm çalışanlar için geçer — çalışan başına olan
 * yalnızca karardır. Üç parça, iki sayfa paylaşır:
 *
 *   PaylasimliOnayAlanlari     — onay penceresindeki iki alan (tarih + ay). Panel (`PaylasimliBelgelerPanel`) kendi onay penceresinde,
 *                                Personel Detayı kendi inceleme penceresinde çizer.
 *   PaylasimliOnayModal        — Panel'in Onayla düğmeleri (toplu / satır): belgede tarih ya da dönem eksikse açılır, tamamsa onay tek tıktır.
 *   PaylasimliAlanDuzenleModal — "Geçerlilik tarihini düzenle" / "Dönemi düzenle" (belge başlığındaki kalem düğmeleri).
 *
 * Saf yardımcılar (`ilkOnayAlanlari`, `onayAlanlariGecerli`, `onayGovdesi`) iki sayfada aynı kuralı verir: değerler belgede zaten varsa
 * aynen ön dolu gelir, DEĞİŞMEDİYSE karar çağrısına eklenmez.
 */
import React, { useState } from "react";
import { Alert, Button, FormGroup, Input, Label, Modal, ModalBody, ModalFooter, ModalHeader, Spinner } from "reactstrap";
import {
  BELGE_TIP, bugunYmd, gecenAyYm, onayGereksinimi, setPaylasimliBelgeDonem, setPaylasimliBelgeGecerlilik,
} from "../../services/personelBelgeService";
import TarihGirdisi from "../Common/TarihGirdisi";
import { useTranslation } from "react-i18next";

const gun = (v) => (v ? String(v).slice(0, 10) : "");
const ay = (v) => (v ? String(v).slice(0, 7) : "");

/** Onay penceresine ilk değerler: belgedeki tarih (bugün ya da sonrasıysa) ve dönem. */
export function ilkOnayAlanlari(belge) {
  const mevcut = gun(belge?.expiryDate);
  return { tarih: mevcut && mevcut >= bugunYmd() ? mevcut : "", donem: ay(belge?.belgeDonemi) };
}

/** Eksik zorunlu alan yok ve girilen değerler backend'in kabul edeceği aralıkta mı (tarih bugün+, ay geçen ay-). */
export function onayAlanlariGecerli(belge, { tarih, donem }) {
  const { tarihGerekli, donemGerekli } = onayGereksinimi(belge);
  if (tarih ? tarih < bugunYmd() : tarihGerekli) return false;
  if (donem ? donem > gecenAyYm() : donemGerekli) return false;
  return true;
}

/** Karar çağrısına eklenecek alanlar: yalnızca belgedekinden FARKLI girilenler (aynı değeri yeniden yazmanın anlamı yok). */
export function onayGovdesi(belge, { tarih, donem }) {
  const govde = {};
  if (tarih && tarih !== gun(belge?.expiryDate)) govde.gecerlilikTarihi = tarih;
  if (donem && donem !== ay(belge?.belgeDonemi)) govde.belgeDonemi = donem;
  return govde;
}

/** İki alan: son geçerlilik tarihi (her paylaşımlı belge) ve kapsadığı ay (yalnızca Hizmet Dökümü). */
export function PaylasimliOnayAlanlari({ belge, tarih, donem, onTarih, onDonem, disabled = false }) {
  const { t: tx } = useTranslation("components");
  const { tarihGerekli, donemGerekli } = onayGereksinimi(belge);
  const hizmetDokumu = Number(belge?.fileTypeId) === BELGE_TIP.SGK_HIZMET_DOKUMU;
  const tarihGecmis = !!tarih && tarih < bugunYmd();
  const donemGecersiz = !!donem && donem > gecenAyYm();
  return (
    <>
      <FormGroup>
        <Label className="small mb-1">
          {tx("paylasimliKarar.sonGecerlilikTarihi")}{tarihGerekli && " *"}
        </Label>
        <TarihGirdisi min={bugunYmd()} value={tarih} disabled={disabled} invalid={tarihGecmis} onChange={onTarih} />
        <div className={`form-text small ${tarihGecmis ? "text-danger" : ""}`}>
          {tarihGecmis ? tx("paylasimliKarar.tarihGecmisteOlamaz") : tx("paylasimliKarar.tarihIpucu")}
        </div>
      </FormGroup>
      {hizmetDokumu && (
        <FormGroup className="mb-0">
          <Label className="small mb-1">
            {tx("paylasimliKarar.donemEtiketi")}{donemGerekli && " *"}
          </Label>
          <Input
            type="month" bsSize="sm" max={gecenAyYm()} value={donem} disabled={disabled} invalid={donemGecersiz}
            onChange={(e) => onDonem(e.target.value)}
          />
          <div className={`form-text small ${donemGecersiz ? "text-danger" : ""}`}>
            {donemGecersiz ? tx("paylasimliKarar.donemBuAyVeSonrasiOlamaz") : tx("paylasimliKarar.donemIpucu")}
          </div>
        </FormGroup>
      )}
    </>
  );
}

/**
 * Panel'in Onayla düğmelerinin açtığı pencere (toplu seçim ya da tek satır). `onConfirm({ gecerlilikTarihi?, belgeDonemi? })`
 * karar çağrısını yapar; hata ve meşgul durumu çağıranda.
 */
export function PaylasimliOnayModal({ belge, kisiSayisi, busy, error, onConfirm, onClose }) {
  const { t: tx } = useTranslation("components");
  const [alanlar, setAlanlar] = useState(() => ilkOnayAlanlari(belge));
  const gecerli = onayAlanlariGecerli(belge, alanlar);
  return (
    <Modal isOpen toggle={busy ? undefined : onClose}>
      <ModalHeader toggle={busy ? undefined : onClose}>{tx("paylasimliKarar.onaylaBaslik")}</ModalHeader>
      <ModalBody>
        {error && <Alert fade={false} color="danger" className="py-2 small">{error}</Alert>}
        <p className="small mb-3">
          <strong>{belge.fileGivenName || belge.belgeTuru}</strong>{" "}
          {tx("paylasimliKarar.onaylanacak", { length: kisiSayisi })}
        </p>
        <PaylasimliOnayAlanlari
          belge={belge} tarih={alanlar.tarih} donem={alanlar.donem} disabled={busy}
          onTarih={(tarih) => setAlanlar((a) => ({ ...a, tarih }))}
          onDonem={(donem) => setAlanlar((a) => ({ ...a, donem }))}
        />
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={busy}>{tx("paylasimliKarar.vazgec")}</Button>
        <Button color="success" disabled={busy || !gecerli} onClick={() => onConfirm(onayGovdesi(belge, alanlar))}>
          {busy ? <Spinner size="sm" /> : tx("paylasimliKarar.onayla")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}

/**
 * `alan` "tarih": PATCH .../gecerlilik · "donem": PATCH .../donem. Belgedeki mevcut değer ön dolu gelir. Tarih için geçmiş,
 * ay için bu ay ve sonrası pencerede kapalı (backend aynı kuralı 400 ile yineler).
 */
export function PaylasimliAlanDuzenleModal({ firmaId, belge, alan, onClose, onDone }) {
  const { t: tx } = useTranslation("components");
  const tarihMi = alan === "tarih";
  const [deger, setDeger] = useState(tarihMi ? ilkOnayAlanlari(belge).tarih : ay(belge?.belgeDonemi));
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);
  const gecersiz = !deger || (tarihMi ? deger < bugunYmd() : deger > gecenAyYm());

  async function kaydet() {
    setBusy(true);
    setError(null);
    try {
      if (tarihMi) await setPaylasimliBelgeGecerlilik(firmaId, belge.belgeId, deger);
      else await setPaylasimliBelgeDonem(firmaId, belge.belgeId, deger);
      onDone(tarihMi ? tx("paylasimliKarar.tarihKaydedildi") : tx("paylasimliKarar.donemKaydedildi"));
    } catch (e) {
      setError(e.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <Modal isOpen toggle={busy ? undefined : onClose}>
      <ModalHeader toggle={busy ? undefined : onClose}>
        {tarihMi ? tx("paylasimliKarar.tarihDuzenleBaslik") : tx("paylasimliKarar.donemDuzenleBaslik")}
      </ModalHeader>
      <ModalBody>
        {error && <Alert fade={false} color="danger" className="py-2 small">{error}</Alert>}
        <p className="small mb-3"><strong>{belge.fileGivenName || belge.belgeTuru}</strong></p>
        <FormGroup className="mb-0">
          <Label className="small mb-1">
            {tarihMi ? tx("paylasimliKarar.sonGecerlilikTarihi") : tx("paylasimliKarar.donemEtiketi")}
          </Label>
          {tarihMi ? (
            <TarihGirdisi min={bugunYmd()} value={deger} disabled={busy} onChange={setDeger} />
          ) : (
            <Input type="month" bsSize="sm" max={gecenAyYm()} value={deger} disabled={busy} onChange={(e) => setDeger(e.target.value)} />
          )}
          <div className="form-text small">
            {tarihMi
              ? tx("paylasimliKarar.tarihDuzenleIpucu", { kisiSayisi: belge.kisiSayisi })
              : tx("paylasimliKarar.donemIpucu")}
          </div>
        </FormGroup>
      </ModalBody>
      <ModalFooter>
        <Button color="light" onClick={onClose} disabled={busy}>{tx("paylasimliKarar.vazgec")}</Button>
        <Button color="primary" disabled={busy || gecersiz} onClick={kaydet}>
          {busy ? <Spinner size="sm" /> : tx("paylasimliKarar.kaydet")}
        </Button>
      </ModalFooter>
    </Modal>
  );
}
